'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Code } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { SearchProductInput } from '@/components/form/search-product-input';
import { useStore } from '@/lib/api/stores';
import { useProductDetails, ProductWithRelations, SearchProductOutputItem } from '@/lib/api/products';
import localeData from '@/data/locale.json';
import { LANGUAGES } from '@/data/languages';

interface LineItem {
  productId: string;
  variantId: string | null;
  priceId: string | null;
  quantity: number;
}

const AUTO_LOCALE = 'auto';

type LanguageCode = keyof (typeof localeData)['primary-dialects'];

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(DirectLinksPage, PERMISSIONS.STORES_VIEW);

function DirectLinksPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['stores', 'common']);
  const { data: store, isLoading: isLoadingStore, error: storeError } = useStore(id!, !!id);

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [currency, setCurrency] = useState(store?.baseCurrency || 'EUR');
  const [locale, setLocale] = useState<LanguageCode | typeof AUTO_LOCALE>(AUTO_LOCALE);
  const [returnUrl, setReturnUrl] = useState('');
  const [buttonText, setButtonText] = useState('Buy Now');
  const [buttonColor, setButtonColor] = useState('#10B981');

  // Track selected products
  const [selectedProducts, setSelectedProducts] = useState<Record<string, ProductWithRelations>>({});

  // Get unique product IDs
  const productIds = useMemo(() => [...new Set(lineItems.map((item) => item.productId))], [lineItems]);

  // Fetch only new products
  const newProductIds = useMemo(
    () => productIds.filter((id) => !selectedProducts[id]),
    [productIds, selectedProducts],
  );

  const { data: newProductsData, isLoading: isLoadingProducts } = useProductDetails(
    newProductIds,
    newProductIds.length > 0,
  );

  // Update selectedProducts when new data arrives
  useEffect(() => {
    if (newProductsData) {
      setSelectedProducts((prev) => ({
        ...prev,
        ...newProductsData.reduce(
          (acc, product) => {
            if (product?.id) {
              acc[product.id] = product;
            }
            return acc;
          },
          {} as Record<string, ProductWithRelations>,
        ),
      }));
    }
  }, [newProductsData]);

  // Update currency if store changes
  useEffect(() => {
    if (store && !store.presentmentCurrencies.includes(currency)) {
      setCurrency(store.baseCurrency);
    }
  }, [store, currency]);

  const handleAddProduct = (product: SearchProductOutputItem) => {
    setLineItems((prev) => [
      ...prev,
      {
        productId: product.id,
        variantId: null,
        priceId: null,
        quantity: 1,
      },
    ]);
  };

  const handleRemoveProduct = (index: number) => {
    const item = lineItems[index];
    setLineItems((prev) => prev.filter((_, i) => i !== index));

    // Check if this product is used elsewhere
    const isProductUsedElsewhere = lineItems.some(
      (otherItem, i) => i !== index && otherItem.productId === item.productId,
    );

    if (!isProductUsedElsewhere) {
      setSelectedProducts((prev) => {
        const next = { ...prev };
        delete next[item.productId];
        return next;
      });
    }
  };

  const handleUpdateLineItem = (index: number, updates: Partial<LineItem>) => {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        return { ...item, ...updates };
      }),
    );
  };

  // Generate dynamic link
  const generateDynamicLink = useCallback(
    (items: LineItem[], storeId: string, currency: string, locale: string) => {
      const validItems = items.filter((item) => item.variantId && item.priceId && item.quantity > 0);

      try {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://api.tagada.io';
        const apiUrl = `${baseUrl}/api/public/v1/checkout/init`;

        const params = new URLSearchParams({
          currency,
          storeId,
          ...(returnUrl && { returnUrl }),
        });

        // Only add items if there are any
        if (validItems.length > 0) {
          const cartItems = validItems.map((item) => ({
            variantId: item.variantId,
            quantity: item.quantity,
            ...(item.priceId && { priceId: item.priceId }),
          }));
          params.append('items', JSON.stringify(cartItems));
        }

        // Only add locale parameter if not automatic
        if (locale !== AUTO_LOCALE) {
          const fullLocale = localeData['primary-dialects'][locale as LanguageCode];
          params.append('locale', fullLocale);
        }

        return `${apiUrl}?${params.toString()}`;
      } catch (error) {
        console.error('Error generating dynamic link:', error);
        return '';
      }
    },
    [returnUrl],
  );

  const [dynamicUrl, setDynamicUrl] = useState('');

  // Update dynamic URL when needed (always generate, even with empty items)
  useEffect(() => {
      const url = generateDynamicLink(lineItems, id!, currency, locale);
      setDynamicUrl(url);
  }, [lineItems, id, currency, locale, generateDynamicLink]);

  // Generate button HTML
  const generateButtonHtml = (url: string) => {
    return `<a href="${url}" style="display:inline-block;padding:12px 24px;background-color:${buttonColor};color:white;text-decoration:none;border-radius:6px;font-family:system-ui,-apple-system,sans-serif;font-weight:500">${buttonText}</a>`;
  };

  // Loading state
  if (isLoadingStore) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] sm:h-8 sm:w-8"></div>
          <p className="text-muted-foreground mt-3 text-xs sm:mt-4 sm:text-sm">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (storeError) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <p className="text-destructive">{t('stores:directLink.errorLoadingStore')}</p>
        </div>
      </div>
    );
  }

  // No store state
  if (!store) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <p className="text-muted-foreground">{t('stores:directLink.storeNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{t('stores:directLink.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('stores:directLink.description')}</p>
        </div>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('stores:directLink.settings')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>{t('stores:directLink.presentmentCurrency')}</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {store.presentmentCurrencies.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('stores:directLink.language')}</Label>
              <Select
                value={locale}
                onValueChange={(value) => setLocale(value as LanguageCode | typeof AUTO_LOCALE)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('stores:directLink.automaticLanguage')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value={AUTO_LOCALE}>{t('stores:directLink.automaticLanguage')}</SelectItem>
                  {Object.entries(localeData['primary-dialects']).map(([code]) => {
                    const language = LANGUAGES[code as keyof typeof LANGUAGES];
                    const label = language ? `${language.nativeName} (${language.name})` : code.toUpperCase();
                    return (
                      <SelectItem key={code} value={code}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground mt-1 text-sm">
                {locale === AUTO_LOCALE
                  ? t('stores:directLink.automaticLanguageDescription')
                  : t('stores:directLink.forceLanguageDescription')}
              </p>
            </div>
          </div>

          <div>
            <Label>{t('stores:directLink.returnUrl')}</Label>
            <Input
              type="url"
              value={returnUrl}
              onChange={(e) => setReturnUrl(e.target.value)}
              placeholder={t('stores:directLink.returnUrlPlaceholder')}
              className="w-full"
            />
            <p className="text-muted-foreground mt-1 text-sm">
              {t('stores:directLink.returnUrlDescription')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Products Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('stores:directLink.products')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {lineItems.length === 0 ? (
              <div className="py-12 text-center">
                <div className="bg-muted mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full">
                  <Plus className="text-muted-foreground h-8 w-8" />
                </div>
                <h3 className="text-sm font-medium">{t('stores:directLink.noProductsAdded')}</h3>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t('stores:directLink.noProductsDescription')}
                </p>
                <div className="mt-6">
                  <SearchProductInput storeId={id!} onSelect={handleAddProduct} />
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {lineItems.map((item, index) => {
                    const product = selectedProducts[item.productId];
                    const isLoading = isLoadingProducts && !product;
                    const selectedVariant = product?.variants?.find((v) => v.id === item.variantId);

                    return (
                      <Card key={index}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Product image */}
                            <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border">
                              {selectedVariant?.imageUrl || product?.variants?.[0]?.imageUrl ? (
                                <img
                                  src={(selectedVariant?.imageUrl || product?.variants?.[0]?.imageUrl) ?? ''}
                                  alt={selectedVariant?.name || product?.name || 'Product'}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="bg-muted flex h-full w-full items-center justify-center">
                                  <span className="text-muted-foreground text-xs">{t('stores:directLink.noImage')}</span>
                                </div>
                              )}
                            </div>

                            {/* Product info */}
                            <div className="min-w-0 flex-1 space-y-3">
                              <div className="flex items-start justify-between">
                                <div className="min-w-0">
                                  <h4 className="truncate font-medium">
                                    {isLoading ? t('stores:directLink.loading') : product?.name}
                                  </h4>
                                  <p className="text-muted-foreground truncate text-sm">
                                    {isLoading ? '...' : selectedVariant?.name}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveProduct(index)}
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              {/* Variant selection */}
                              {!isLoading && product && (
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                  <div>
                                    <Label className="text-xs">{t('stores:directLink.variant')}</Label>
                                    <Select
                                      value={item.variantId || ''}
                                      onValueChange={(variantId) => {
                                        handleUpdateLineItem(index, {
                                          variantId,
                                          priceId: null,
                                        });
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder={t('stores:directLink.selectVariant')} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {product.variants?.map((variant) => (
                                          <SelectItem key={variant.id} value={variant.id}>
                                            {variant.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  {/* Price selection */}
                                  {item.variantId && (
                                    <div>
                                      <Label className="text-xs">{t('stores:directLink.price')}</Label>
                                      <Select
                                        value={item.priceId || ''}
                                        onValueChange={(priceId) => {
                                          handleUpdateLineItem(index, { priceId });
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder={t('stores:directLink.selectPrice')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {product.variants
                                            ?.find((v) => v.id === item.variantId)
                                            ?.prices?.map((price) => {
                                              const currencyOption = price.currencyOptions[currency];
                                              if (!currencyOption) return null;
                                              return (
                                                <SelectItem key={price.id} value={price.id}>
                                                  {currencyOption.amount / 100} {currency}
                                                </SelectItem>
                                              );
                                            })}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}

                                  {/* Quantity */}
                                  <div>
                                    <Label className="text-xs">{t('stores:directLink.quantity')}</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        handleUpdateLineItem(index, {
                                          quantity: parseInt(e.target.value) || 1,
                                        })
                                      }
                                      disabled={isLoading}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <SearchProductInput storeId={id!} onSelect={handleAddProduct} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Generated Links Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('stores:directLink.generatedLinks')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dynamic Link Section */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h4 className="font-medium">{t('stores:directLink.dynamicCheckoutUrl')}</h4>
                <p className="text-muted-foreground text-sm">
                  {t('stores:directLink.dynamicUrlDescription')}
                </p>
              </div>
              <div className="text-muted-foreground flex items-center text-sm">
                <span className="mr-2 inline-block h-2 w-2 rounded-full bg-green-500"></span>
                {t('stores:directLink.alwaysAvailable')}
              </div>
            </div>
              <div className="space-y-2">
              {lineItems.length === 0 && (
                <p className="text-muted-foreground mb-2 text-sm">
                  {t('stores:directLink.emptyCheckoutHint')}
                </p>
              )}
                <div className="flex items-center gap-2">
                  <Input value={dynamicUrl} readOnly />
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      await navigator.clipboard.writeText(dynamicUrl);
                      toast.success(t('stores:directLink.dynamicUrlCopied'));
                    }}
                  >
                    {t('stores:directLink.copy')}
                  </Button>
                </div>
              </div>
          </div>

          {/* Buy Now Button Section */}
          <div className="border-t pt-6">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h4 className="font-medium">{t('stores:directLink.buyNowButton')}</h4>
                <p className="text-muted-foreground text-sm">{t('stores:directLink.embedOnWebsite')}</p>
              </div>
              <div className="text-muted-foreground flex items-center text-sm">
                <Code className="mr-2 h-4 w-4" />
                {t('stores:directLink.htmlCode')}
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>{t('stores:directLink.buttonText')}</Label>
                  <Input
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    placeholder={t('stores:directLink.buyNowPlaceholder')}
                  />
                </div>
                <div>
                  <Label>{t('stores:directLink.buttonColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                      className="h-10 w-12 p-1"
                    />
                    <Input
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                      placeholder="#000000"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>{t('stores:directLink.preview')}</Label>
                <div className="bg-muted flex items-center justify-center rounded-md p-4">
                  <div dangerouslySetInnerHTML={{ __html: generateButtonHtml(dynamicUrl || '#') }} />
                </div>
              </div>

              <div>
                <Label>{t('stores:directLink.htmlCode')}</Label>
                <div className="relative">
                  <Textarea
                    value={generateButtonHtml(dynamicUrl)}
                    readOnly
                    className="font-mono text-sm"
                    rows={3}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute right-2 top-2"
                    onClick={async () => {
                      await navigator.clipboard.writeText(generateButtonHtml(dynamicUrl));
                      toast.success(t('stores:directLink.htmlCodeCopied'));
                    }}
                  >
                    {t('stores:directLink.copy')}
                  </Button>
                </div>
                <p className="text-muted-foreground mt-2 text-sm">
                  {t('stores:directLink.copyPasteInstructions')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
